import { useMemo, useState } from 'react'
import { MobileContentGems } from '@mc/api'
import { useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import { Section } from 'components/Section'
import { DynamicHeroValues } from 'libs/dynamic-hero'
import {
  trackViewLessonPlanClicked,
  trackLessonPopoverClosed,
  ClassPageLocations,
} from '../../../events'
import { Title } from '../../Title'
import { personalizedHero } from '../../utils'
import { ContentDesktop } from './ContentDesktop'
import { ContentMobile } from './ContentMobile'
import { LessonPlanPopover } from './LessonPlanPopover'

const applyContentGemImageOverrides = (
  gems: MobileContentGems[],
  overrides: DynamicHeroValues['contentGemImages'],
  breakpoint: 'desktop' | 'mobile',
) => {
  if (!overrides || !gems?.length) return gems

  return gems.map((gem) => {
    const imageUrl = overrides[String(gem.position)]?.[breakpoint]
    return imageUrl ? { ...gem, imageUrl } : gem
  })
}

export const SkillsYoullLearnSection = () => {
  const { t } = useTranslation('@mc/next')
  const { mobileContentGems, savedDynamicHero } = useStaticData<{
    mobileContentGems: MobileContentGems[]
    savedDynamicHero: DynamicHeroValues
  }>()

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const overrides = personalizedHero(savedDynamicHero)?.contentGemImages

  const dynamicMobileGemsDesktop = useMemo(
    () =>
      applyContentGemImageOverrides(mobileContentGems, overrides, 'desktop'),
    [mobileContentGems, overrides],
  )

  const dynamicMobileGemsMobile = useMemo(
    () => applyContentGemImageOverrides(mobileContentGems, overrides, 'mobile'),
    [mobileContentGems, overrides],
  )

  if (!mobileContentGems?.length) {
    return null
  }

  const handleSubtitleClick = () => {
    setIsPopoverOpen(true)
    trackViewLessonPlanClicked()
  }

  const closePopover = () => {
    setIsPopoverOpen(false)
    trackLessonPopoverClosed()
  }

  return (
    <Section noAnimation location={ClassPageLocations.SKILLS_YOU_LEARN}>
      <div className='row mc-mb-3'>
        <div className='col-12'>
          <Title>{t('course.sections.skillsYoullLearn.title')}</Title>
          <button
            onClick={handleSubtitleClick}
            className='mc-text-h6 mc-p-0 mc-clickable'
          >
            {t('course.sections.skillsYoullLearn.subtitle')}
          </button>
        </div>
      </div>

      <div className='d-none d-sm-block'>
        <ContentDesktop mobileContentGems={dynamicMobileGemsDesktop} />
      </div>
      <div className='d-sm-none'>
        <ContentMobile mobileContentGems={dynamicMobileGemsMobile} />
      </div>

      <LessonPlanPopover isOpen={isPopoverOpen} onClose={closePopover} />
    </Section>
  )
}
